import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { useQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
  GetCouponDocument,
  UpdateCouponDocument,
  DeleteCouponDocument,
  type GetCouponQuery,
  type CouponStatus,
  type CouponUsageType,
  type UpdateCouponMutation,
  type UpdateCouponMutationVariables,
  type DeleteCouponMutation,
  type DeleteCouponMutationVariables,
} from '@/generated/root-types';
import { useAuth } from '@/app/auth/AuthContext';
import { isSuperAdmin } from '@/utils/roles';
import { useNotification } from '@/app/contexts/NotificationContext';
import { errorsFromPayload } from '@/utils/mutationErrors';
import {
  COUPON_STATUSES,
  COUPON_USAGE_TYPES,
  couponDiscountTypeLabel,
  couponStatusLabel,
  couponTypeLabel,
  couponUsageTypeLabel,
  formatCouponDiscount,
  joinCsvList,
  parseCsvList,
} from '@/utils/couponLabels';
import {
  detailBackButtonSx,
  detailFieldGridSx,
  detailFieldSx,
  detailFullRowSx,
  detailHeaderRowSx,
  detailPageContainerSx,
  detailTitleSx,
} from '@/utils/detailPageLayout';

function toDatetimeLocalValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function CouponDetail() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatDateTime = (iso: string) => new Date(iso).toLocaleString(locale);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const superAdmin = isSuperAdmin(user);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<CouponStatus>('ACTIVE');
  const [usageType, setUsageType] = useState<CouponUsageType>('INTERNAL');
  const [maxRedemptions, setMaxRedemptions] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [allowedPlanCodes, setAllowedPlanCodes] = useState('');
  const [allowedProductSkus, setAllowedProductSkus] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data, loading, error, refetch } = useQuery<GetCouponQuery>(GetCouponDocument, {
    variables: { id: id! },
    skip: !id,
  });

  const coupon = data?.coupon;

  useEffect(() => {
    if (!coupon) return;
    setName(coupon.name);
    setDescription(coupon.description ?? '');
    setStatus(coupon.status);
    setUsageType(coupon.usageType);
    setMaxRedemptions(coupon.maxRedemptions != null ? String(coupon.maxRedemptions) : '');
    setExpiryDate(toDatetimeLocalValue(coupon.expiryDate));
    setAllowedPlanCodes(joinCsvList(coupon.allowedPlanCodes));
    setAllowedProductSkus(joinCsvList(coupon.allowedProductSkus));
  }, [coupon]);

  const [updateCoupon, { loading: saving }] = useMutation<
    UpdateCouponMutation,
    UpdateCouponMutationVariables
  >(UpdateCouponDocument);

  const [deleteCoupon, { loading: deleting }] = useMutation<
    DeleteCouponMutation,
    DeleteCouponMutationVariables
  >(DeleteCouponDocument);

  const handleSave = async () => {
    if (!id) return;
    const max = maxRedemptions.trim() === '' ? null : parseInt(maxRedemptions, 10);
    if (maxRedemptions.trim() && (max === null || Number.isNaN(max))) {
      showError(t('errors.generic'));
      return;
    }

    try {
      const res = await updateCoupon({
        variables: {
          input: {
            id,
            name,
            description: description || undefined,
            status,
            usageType,
            maxRedemptions: max ?? undefined,
            expiryDate: expiryDate ? new Date(expiryDate).toISOString() : undefined,
            allowedPlanCodes: parseCsvList(allowedPlanCodes),
            allowedProductSkus: parseCsvList(allowedProductSkus),
          },
        },
      });
      const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
      if (top?.length) {
        showError(top.map((e) => e.message).join(' '));
        return;
      }
      const pe = errorsFromPayload(res.data?.updateCoupon);
      if (pe) {
        showError(pe);
        return;
      }
      showSuccess(t('common.saved'));
      await refetch();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      const res = await deleteCoupon({ variables: { input: { id } } });
      const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
      if (top?.length) {
        showError(top.map((e) => e.message).join(' '));
        return;
      }
      const pe = errorsFromPayload(res.data?.deleteCoupon);
      if (pe || !res.data?.deleteCoupon?.boolean) {
        showError(pe || t('errors.generic'));
        return;
      }
      showSuccess(t('common.deleted'));
      navigate('/coupons');
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  if (loading) {
    return (
      <Box sx={{ ...detailPageContainerSx, display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !coupon) {
    return (
      <Box sx={detailPageContainerSx}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/coupons')}>
          {t('common.back')}
        </Button>
        <Typography color="error" sx={{ mt: 2 }}>
          {error?.message ?? t('errors.generic')}
        </Typography>
      </Box>
    );
  }

  const discountValue = coupon.discountValue;

  return (
    <Box sx={detailPageContainerSx}>
      <Box sx={detailHeaderRowSx}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/coupons')}
          sx={detailBackButtonSx}
        >
          {t('common.back')}
        </Button>
        <Typography variant="h4" component="h1" sx={detailTitleSx}>
          {coupon.code} — {coupon.name}
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        {t('coupons.pricingReadOnly')}
      </Alert>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, width: '100%', boxSizing: 'border-box' }}
      >
        <Box sx={detailFieldGridSx}>
          <TextField
            sx={detailFieldSx}
            label={t('masterData.code')}
            value={coupon.code}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            label={t('common.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            disabled={!superAdmin}
          />
          <TextField
            sx={detailFieldSx}
            label={t('common.type')}
            value={couponTypeLabel(t, coupon.type)}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            label={t('coupons.duration')}
            value={coupon.duration != null ? String(coupon.duration) : '–'}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            label={t('coupons.discountType')}
            value={couponDiscountTypeLabel(t, coupon.discountType)}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            label={t('coupons.discountValue')}
            value={formatCouponDiscount(t, coupon.discountType, discountValue, locale)}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            select
            label={t('common.status')}
            value={status}
            onChange={(e) => setStatus(e.target.value as CouponStatus)}
            fullWidth
            disabled={!superAdmin}
          >
            {COUPON_STATUSES.map((x) => (
              <MenuItem key={x} value={x}>
                {couponStatusLabel(t, x)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            sx={detailFieldSx}
            select
            label={t('coupons.usageType')}
            value={usageType}
            onChange={(e) => setUsageType(e.target.value as CouponUsageType)}
            fullWidth
            helperText={t('coupons.usageTypeHint')}
            disabled={!superAdmin}
          >
            {COUPON_USAGE_TYPES.map((x) => (
              <MenuItem key={x} value={x}>
                {couponUsageTypeLabel(t, x)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            sx={detailFieldSx}
            label={t('coupons.redemptionCount')}
            value={String(coupon.redemptionCount)}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            label={t('coupons.maxRedemptions')}
            value={maxRedemptions}
            onChange={(e) => setMaxRedemptions(e.target.value)}
            type="number"
            inputProps={{ min: 1 }}
            fullWidth
            disabled={!superAdmin}
          />
          <TextField
            sx={detailFieldSx}
            label={t('coupons.expiryDate')}
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
            type="datetime-local"
            fullWidth
            InputLabelProps={{ shrink: true }}
            disabled={!superAdmin}
          />
          <TextField
            sx={detailFieldSx}
            label={t('common.created')}
            value={formatDateTime(coupon.createdAt)}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            label={t('common.updated')}
            value={formatDateTime(coupon.updatedAt)}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={{ ...detailFieldSx, ...detailFullRowSx }}
            label={t('common.description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            disabled={!superAdmin}
          />
          <TextField
            sx={{ ...detailFieldSx, ...detailFullRowSx }}
            label={t('coupons.allowedPlanCodes')}
            value={allowedPlanCodes}
            onChange={(e) => setAllowedPlanCodes(e.target.value)}
            fullWidth
            helperText={t('coupons.allowedPlanCodesHint')}
            disabled={!superAdmin}
          />
          <TextField
            sx={{ ...detailFieldSx, ...detailFullRowSx }}
            label={t('coupons.allowedProductSkus')}
            value={allowedProductSkus}
            onChange={(e) => setAllowedProductSkus(e.target.value)}
            fullWidth
            helperText={t('coupons.allowedProductSkusHint')}
            disabled={!superAdmin}
          />
          {superAdmin && (
            <Box sx={{ ...detailFullRowSx, display: 'flex', gap: 1, flexWrap: 'wrap', pt: 0.5 }}>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {t('common.save')}
              </Button>
              <Button color="error" onClick={() => setDeleteOpen(true)}>
                {t('common.delete')}
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>{t('coupons.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>{t('coupons.confirmDeleteBody')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>{t('common.cancel')}</Button>
          <Button color="error" onClick={handleDelete} disabled={deleting}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
